import { Fragment, useState } from 'react';
import { ArrowLeftRight, ChevronDown, ChevronRight } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useAcceptConversionSuggestion,
  useConversionSuggestionFundingChain,
  useConversionSuggestions,
} from '@/hooks/react-query/useCurrencyConversions';
import type { ConversionProposal } from '@/types/currency-conversion';
import { ConversionFundingChain } from '@/components/transactions/ConversionFundingChain';
import { formatCurrency, formatDate, formatFxRate } from '@/utils/formatters';

export interface ConversionSuggestionsCardProps {
  fundId: string;
}

function describeTrade(proposal: ConversionProposal): string {
  const given = formatCurrency(proposal.from_amount, {
    currency: proposal.from_currency,
  });
  const received = formatCurrency(proposal.to_amount, {
    currency: proposal.to_currency,
  });
  return `${given} → ${received}`;
}

function ProposalFundingChain({
  fundId,
  tradeReference,
}: {
  fundId: string;
  tradeReference: string;
}) {
  const chain = useConversionSuggestionFundingChain(fundId, tradeReference);

  if (chain.isLoading) {
    return (
      <p className="py-2 text-sm text-muted-foreground">Reading the trip...</p>
    );
  }
  if (!chain.data) {
    return (
      <p className="py-2 text-sm text-muted-foreground">
        No bank lines carry this trade's reference.
      </p>
    );
  }
  return <ConversionFundingChain fundId={fundId} chain={chain.data} />;
}

/**
 * Hidden when there is nothing to propose. A failed read says so instead -
 * silence there would read as "all recorded".
 */
export function ConversionSuggestionsCard({
  fundId,
}: ConversionSuggestionsCardProps) {
  const suggestions = useConversionSuggestions(fundId);
  const accept = useAcceptConversionSuggestion(fundId);
  const proposals = suggestions.data ?? [];
  const [openTrades, setOpenTrades] = useState<Set<string>>(() => new Set());

  const toggle = (tradeReference: string) =>
    setOpenTrades((open) => {
      const next = new Set(open);
      if (!next.delete(tradeReference)) next.add(tradeReference);
      return next;
    });

  if (suggestions.isError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          The conversions on this fund's bank statement could not be read. Any
          the bank has already executed are still waiting to be recorded.
        </AlertDescription>
      </Alert>
    );
  }

  if (suggestions.isLoading || proposals.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ArrowLeftRight className="h-4 w-4" />
          Conversions on the bank statement
        </CardTitle>
        <CardDescription>
          The bank has already executed these and both legs are still
          outstanding. Recording one settles the two bank lines behind it; the
          draft still waits for approval.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-px" />
              <TableHead>Date</TableHead>
              <TableHead>Trade</TableHead>
              <TableHead>Reference</TableHead>
              <TableHead className="text-right">Rate</TableHead>
              <TableHead className="w-px" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {proposals.map((proposal) => {
              const isOpen = openTrades.has(proposal.trade_reference);
              return (
                <Fragment key={proposal.trade_reference}>
                  <TableRow>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        aria-expanded={isOpen}
                        aria-label={`Bank lines behind ${proposal.trade_reference}`}
                        onClick={() => toggle(proposal.trade_reference)}
                      >
                        {isOpen ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatDate(proposal.conversion_date)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {describeTrade(proposal)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {proposal.trade_reference}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatFxRate(proposal.bank_rate)}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        disabled={accept.isPending}
                        onClick={() => accept.mutate(proposal.trade_reference)}
                      >
                        Record
                      </Button>
                    </TableCell>
                  </TableRow>
                  {isOpen && (
                    <TableRow className="hover:bg-transparent">
                      <TableCell />
                      <TableCell colSpan={5} className="pt-0">
                        <ProposalFundingChain
                          fundId={fundId}
                          tradeReference={proposal.trade_reference}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
